/**
 * `/invite/$token` — accept an invitation, signed in or not. Plan §2 U64.
 *
 * Signed out, the screen shows the sign-in and sign-up forms in place and stays on the token.
 * Signed in, it asks once and accepts. The org header is set only from the accept's answer,
 * which the server read from the database — never from the URL.
 */
import { useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { Button, Surface } from "@packagepage/ui";

import { authClient } from "../api/auth.js";
import { problemOf, setActiveOrg } from "../api/client.js";
import { api } from "../api/procedures.js";
import { SignIn } from "./SignIn.js";
import { ROLE_LABELS } from "./TeamSettings.js";

import "@packagepage/ui/tokens.css";
import "@packagepage/ui/surface.css";
import "@packagepage/ui/button.css";
import "./auth.css";

export function InviteAccept({ token }: { readonly token: string }): ReactNode {
  const navigate = useNavigate();
  const session = authClient.useSession();
  const [mode, setMode] = useState<"signin" | "signup">("signup");
  const accept = useMutation({
    mutationFn: () => api.acceptInvite(token),
    onSuccess: async (joined) => {
      setActiveOrg(joined.orgId);
      await navigate({ to: "/" });
    },
  });

  if (session.isPending) {
    return (
      <div className="pp-auth" data-screen-label="Invitation">
        <div className="pp-auth__panel">
          <p className="pp-auth__lede">Checking whether you are signed in…</p>
        </div>
      </div>
    );
  }

  const user = session.data?.user;
  if (user === undefined) {
    return (
      <SignIn
        mode={mode}
        onSignedIn={() => void session.refetch()}
        onSwitch={() => setMode(mode === "signup" ? "signin" : "signup")}
      />
    );
  }

  const problem = accept.isError ? problemOf(accept.error) : null;

  return (
    <div className="pp-auth" data-screen-label="Invitation">
      <div className="pp-auth__panel">
        <Surface className="pp-auth__card">
          <div>
            <div className="pp-auth__title">Join your team on packagepage</div>
            <p className="pp-auth__lede">
              You are signed in as {user.email}. Accepting adds you to the organisation
              that invited this address, with the role the invitation names.
            </p>
          </div>

          {accept.isError && (
            <Surface tone="error" emphasis="filled">
              {problem?.detail ??
                "We could not reach the server. Nothing was changed — try again."}
            </Surface>
          )}
          {accept.isSuccess && (
            <p className="pp-auth__lede">
              You joined {accept.data.orgName} as {ROLE_LABELS[accept.data.role]}.
            </p>
          )}

          <Button
            variant="primary"
            loading={accept.isPending}
            onClick={() => accept.mutate()}
          >
            {accept.isPending ? "Joining…" : "Accept invitation"}
          </Button>
          {problem?.code === "INVITE_EMAIL_MISMATCH" && (
            <Button
              variant="secondary"
              onClick={() => {
                void authClient.signOut().then(() => session.refetch());
              }}
            >
              Sign out and use the invited address
            </Button>
          )}
        </Surface>
      </div>
    </div>
  );
}
